import React, { useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import gql from 'graphql-tag';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { LaneType } from 'common-types';
import { getClient } from 'lane-shared/apollo';
import { ValidationErrorContext } from 'lane-shared/contexts';
import { ValidationErrorContextType } from 'lane-shared/contexts/ValidationErrorContext';
import { PublicUserFragment } from 'lane-shared/graphql/fragments';
import {
  createChannelIntegration as createChannelIntegrationMutation,
  softDeleteChannelIntegration,
  updateChannelIntegration as updateChannelIntegrationMutation,
  updateChannelGroupRoles as updateChannelGroupRolesMutation,
  unPublishContent,
} from 'lane-shared/graphql/mutation';
import { pause } from 'async-timing';
import { safeConvertToUUID, convertToUUID, convertTo62 } from 'uuid-encoding';
import constructProperty from 'lane-shared/helpers/content/constructProperty';
import getIntegrationDefinition from 'lane-shared/helpers/integrations/getIntegrationDefinition';
import {
  useFlag,
  useChannelServiceRequestFeaturesContext,
  useSetContextualLDAttributes
} from 'lane-shared/hooks';
import { createShapeFromProperties } from 'lane-shared/properties';
import { FeatureFlag } from 'constants-flags';
import 'lane-shared/types/baseTypes';
import { IntegrationProviderEnum } from 'constants-integrations';

import ErrorMessage from 'components/general/ErrorMessage';
import Loading from 'components/general/Loading';
import ChannelIntegrationEditorAutoRenderer from 'components/integrations/ChannelIntegrationEditor/ChannelIntegrationEditorAutoRenderer';
import {
  ChannelIntegration,
  ChannelIntegrationEditorProps,
} from 'components/integrations/ChannelIntegrationEditor/ChannelIntegrationEditorProps';
import { DeleteIntegrationModal } from 'components/integrations/ChannelIntegrationEditor/DeleteIntegrationModal';
import AdminPage from 'components/layout/AdminPage';

import { history, validateNestedConfig } from '../../helpers';
import ChannelIntegrationEditorRenderers from '../integrations/ChannelIntegrationEditor';
import IntegrationDropdown from './IntegrationDropdown';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { useGetAuthToken } from 'hooks/useGetAuthToken';

import styles from './ChannelIntegrationEdit.scss';
import { CHANNEL_INTEGRATION_PERMISSION_UPDATES } from 'constants-permissions';
import { TESTABLE_INTEGRATIONS_CONFIG } from 'lane-shared/helpers/constants/integrations/Common';
import { AccessControlServiceEntity } from 'lane-shared/helpers/integrations/AccessManagement/accessControl';
import {
  CardFormatEntity,
  CardFormatDropdownOptions,
} from 'lane-shared/helpers/integrations/Kastle/definition';
import { useQuery } from '@apollo/client';
import { contentsForIntegration } from 'lane-shared/graphql/content';
import type { Content } from 'graphql-query-contracts';
import { Button, Flex, H3 } from 'design-system-web';
import { Chip, ChipStyle } from 'components/ads';
import { cloneDeep } from '@apollo/client/utilities';
import { IntegrationDefinition } from 'lane-shared/types/integrations/IntegrationDefinition';

const getIntegration = gql`
  ${PublicUserFragment}

  query getIntegrationForEdit($channelId: UUID!, $id: UUID!) {
    availableIntegrations(channelId: $channelId, search: { _id: $id }) {
      _id
      _created
      _updated
      _createdBy {
        ...PublicUserFragment
      }
      _updatedBy {
        ...PublicUserFragment
      }
      name
      category
      type
      settings
      properties
      platforms
      workflows
    }
  }
`;

const verifyConfigQuery = gql`
  query VerifyConfig($provider: String!, $channelId: UUID!, $config: JSON!) {
    verifyConfig(provider: $provider, channelId: $channelId, config: $config) {
      data
    }
  }
`;

type TenantMappings = {
  vtsTenantId: string;
  geneaTenantId?: string;
  prevIgsValue?: string;
  vtsTenantIDs?: string[];
};

/**
 * Keeps Kastle wallet/card-format settings consistent:
 * When Google Wallet is enabled, only Aliro is valid — auto-switch away from Symmetric.
 */
export function normalizeKastleWalletSettings(settings: any): any {
  if (!settings) {
    return settings;
  }

  const next = { ...settings };

  if (next.isGoogleWalletEnabled && next.cardFormat?.value === CardFormatEntity.Symmetric) {
    next.cardFormat = CardFormatDropdownOptions.find(o => o.value === CardFormatEntity.Aliro);
  }

  return next;
}

export default function ChannelIntegrationEdit({
  channelIntegration,
  forCreate,
}: ChannelIntegrationEditorProps) {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  // useChannelAdminContext returns a UUID _id, but the LaunchDarkly channel
  // targeting rule keys on the base62 form. convertTo62 is idempotent, so this
  // is safe whether _id is a UUID or already base62. Passing the raw UUID here
  // caused channel-targeted flags (e.g. Kastle Google Wallet) to evaluate false
  // on SPA navigation until a refresh.
  useSetContextualLDAttributes({
    focusedChannelId: channel?._id ? convertTo62(channel._id) : undefined,
  });
  const { refetchChannelFeatures } = useChannelServiceRequestFeaturesContext();
  const getAuthToken = useGetAuthToken();
  const [_channelIntegration, setChannelIntegration] = useState<any>(null);
  const [integration, setIntegration] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [integrationIsDeleted, setIntegrationIsDeleted] = useState(false);
  const [isConfigActive, setIsConfigActive] = useState(!integrationIsDeleted);
  const [validationError, setValidationError] =
    useState<ValidationErrorContextType>(null);
  const [isTestConfigButtonVisible, setIsTestConfigButtonVisible] =
    useState(false);
  const [isChannelIntegrationUpdated, setIsChannelIntegrationUpdated] =
    useState(false);

  const ChannelIntegrationEditorRenderer = integration
    ? // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      ChannelIntegrationEditorRenderers[integration.name] ||
      ChannelIntegrationEditorAutoRenderer
    : null;

  const deleteIntegrationFeatureEnabled = useFlag(
    FeatureFlag.DeleteChannelIntegrationAccess,
    true
  );

  const geneaVisitorFeatureEnabled = useFlag(
    FeatureFlag.GeneaVisitorManagement,
    false
  );
  const kastleAppleWalletFFEnabled = useFlag(
    FeatureFlag.EnableKastleAppleWallet,
    false
  );
  const kastleGoogleWalletFFEnabled = useFlag(
    FeatureFlag.EnableKastleGoogleWallet,
    false
  );
  const cardFormatFFEnabled = useFlag(
    FeatureFlag.EnableKastleSubscription,
    false
  );

  const isBEFFEnabled = useFlag(
    FeatureFlag.EnableSSOAndMultiBuildingForBE,
    false
  );

  const isBEWebhookEnabled = useFlag(
    FeatureFlag.WebHookNotificationForBE,
    false
  );

  const vantifyWebhookNotificationsEnabledFF =
    useFlag(
      FeatureFlag.SquadTechnicalSolutionsWebHookNotificationForVantify,
      false
    );

  const tenantMappingConvertToUUIDEnabled = useFlag(
    FeatureFlag.TenantMappingConvertToUUID,
    false
  );

  const definition = useMemo(() => {
    if (!integration) return null;

    const def = getIntegrationDefinition(integration.name);

    if (def?.name !== IntegrationProviderEnum.Kastle) {
      return def;
    }

    const kastleDef = cloneDeep(def);

    kastleDef.properties.isAppleWalletEnabled.hidden = !kastleAppleWalletFFEnabled;
    kastleDef.properties.isGoogleWalletEnabled.hidden = !kastleGoogleWalletFFEnabled;

    const cardFormatProperty = kastleDef.properties.cardFormat;

    if (cardFormatFFEnabled && cardFormatProperty) {
      const settings = _channelIntegration?.settings ?? {};
      const isGoogleEnabled = !!settings.isGoogleWalletEnabled;
      const isAppleEnabled = !!settings.isAppleWalletEnabled;
      const anyWalletEnabled = isGoogleEnabled || isAppleEnabled;

      cardFormatProperty.hidden = false;
      cardFormatProperty.disabled = !anyWalletEnabled;
      cardFormatProperty.validators = [
        {
          name: 'In',
          value: isGoogleEnabled
            ? CardFormatDropdownOptions.filter(o => o.value === CardFormatEntity.Aliro)
            : CardFormatDropdownOptions,
        },
      ];

      kastleDef.properties.isGoogleWalletEnabled.disabled = false;
      kastleDef.properties.isGoogleWalletEnabled.description = isGoogleEnabled
        ? 'web.admin.channel.integrations.kastle.googleWallet.requiresAliro'
        : undefined;
    } else if (cardFormatProperty) {
      cardFormatProperty.hidden = true;
      kastleDef.properties.isGoogleWalletEnabled.disabled = false;
      kastleDef.properties.isGoogleWalletEnabled.description = undefined;
    }

    return kastleDef;
  }, [
    integration,
    kastleAppleWalletFFEnabled,
    kastleGoogleWalletFFEnabled,
    cardFormatFFEnabled,
    _channelIntegration?.settings?.isGoogleWalletEnabled,
    _channelIntegration?.settings?.isAppleWalletEnabled,
  ]);

  function updateChannelIntegration(props: ChannelIntegration) {
    setChannelIntegration((prevState: Partial<ChannelIntegration>) => {
      const next = { ...prevState, ...props };

      // Keep the Kastle wallet/card-format fields internally consistent on every change.
      if (
        cardFormatFFEnabled &&
        definition?.name === IntegrationProviderEnum.Kastle &&
        props.settings
      ) {
        next.settings = normalizeKastleWalletSettings(next.settings);
      }

      return next;
    });
  }

  async function loadIntegration(id: LaneType.UUID) {
    setLoading(true);
    setIntegration(null);

    if (TESTABLE_INTEGRATIONS_CONFIG[integration?.name]) {
      setIsTestConfigButtonVisible(true);
    } else {
      setIsTestConfigButtonVisible(false);
    }

    const { data } = await getClient().query({
      query: getIntegration,
      variables: {
        id,
        channelId: channel?._id,
      },
    });

    setIntegration(data.availableIntegrations?.[0]);

    setLoading(false);
  }

  const { data: integrationContent } = useQuery<{
    contentsForIntegration: Content[];
  }>(contentsForIntegration, {
    variables: {
      _id: safeConvertToUUID(channelIntegration?._id),
    },
    skip: !channelIntegration?._id,
  });

  useEffect(() => {
    if (!channel?._id) return;
    // if there is an integration selected, and its not loaded,
    // or not the one that's loaded. load one.

    if (
      (channelIntegration?.integration?._id && !integration) ||
      channelIntegration?.integration?._id !== integration?._id
    ) {
      loadIntegration(channelIntegration.integration._id);
    }

    if (channelIntegration?._id) {
      setChannelIntegration(channelIntegration);
    }

    if (TESTABLE_INTEGRATIONS_CONFIG[channelIntegration?.integration?.name]) {
      setIsTestConfigButtonVisible(true);
    } else {
      setIsTestConfigButtonVisible(false);
    }

    if (
      channelIntegration?.deletedAt &&
      channelIntegration.deletedAt !== null
    ) {
      setIntegrationIsDeleted(true);
      setIsConfigActive(false);
    }
  }, [channelIntegration?._id, channel?._id]);

  function changeIntegration(integration: any) {
    setIntegration(integration);

    const definition = getIntegrationDefinition(integration.name);

    // setup the default settings of this integration based on its definition
    const settings = {};

    Object.entries(definition.properties).forEach(
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      ([key, property]) => (settings[key] = constructProperty(property))
    );

    updateChannelIntegration({
      settings,
      // @ts-expect-error ts-migrate(2740) FIXME: Type '{ _id: any; }' is missing the following prop... Remove this comment to see the full error message
      integration: {
        _id: integration._id,
      },
    });

    if (TESTABLE_INTEGRATIONS_CONFIG[integration?.name]) {
      setIsTestConfigButtonVisible(true);
    } else {
      setIsTestConfigButtonVisible(false);
    }
  }

  const validateNestedBEConfig = (cloneDefinition: IntegrationDefinition) => {
    if (isBEFFEnabled) {
      delete cloneDefinition.properties?.buildingId;
      delete _channelIntegration.settings?.buildingId;
    } else {
      delete cloneDefinition.properties?.buildingIds;
      delete _channelIntegration.settings?.buildingIds;

      if (
        cloneDefinition.name ===
        IntegrationProviderEnum.BuildingEnginesPrismVisitors
      ) {
        delete cloneDefinition.properties?.username;
        delete _channelIntegration.settings?.username;
        delete cloneDefinition.properties?.password;
        delete _channelIntegration.settings?.password;
      }
    }

    if (!_channelIntegration.settings?.buildingIds?.length) {
      delete _channelIntegration.settings.buildingIds;
    }

    if (
      (!isBEWebhookEnabled || !isBEFFEnabled) &&
      cloneDefinition.name === IntegrationProviderEnum.BuildingEnginesPrism
    ) {
      delete _channelIntegration.settings?.disableWebhookNotification;
    }

  };

  const validateVantifyConfig = (cloneDefinition: IntegrationDefinition) => {

    delete cloneDefinition.properties?.loadSettingButton;
    delete cloneDefinition.properties?.subHeading;
    delete _channelIntegration.settings?.loadSettingButton;
    delete _channelIntegration.settings?.subHeading;
    
    const autoApproveWorkOrders = _channelIntegration?.settings?.autoApproveWorkOrders;

    if (!autoApproveWorkOrders) {
      delete cloneDefinition.properties?.approvedDomains;
      delete _channelIntegration.settings?.approvedDomains;
    }
  };

  const validateKastleConfig = (cloneDefinition: IntegrationDefinition) => {
    if (cardFormatFFEnabled) {
      const property = cloneDefinition.properties?.cardFormat;
      if (property) {
        const isGoogleEnabled = !!_channelIntegration?.settings?.isGoogleWalletEnabled;
        if (isGoogleEnabled) {
          property.validators = [
            ...(property.validators ?? []),
            { name: 'Required', value: true },
          ];
        } else {
          // cardFormat is absent. Exclude it from validation when not required.
          delete cloneDefinition.properties.cardFormat;
        }
      }
    } else {
      delete cloneDefinition.properties?.cardFormat;
    }
  };

  function validateChannelIntegrationSettings(): boolean {
    if (!definition) {
      return false;
    }

    try {
      const workingDefinition =
        definition.name === IntegrationProviderEnum.BuildingEnginesPrism ||
        definition.name === IntegrationProviderEnum.BuildingEnginesPrismVisitors ||
        definition.name === IntegrationProviderEnum.Vantify ||
        definition.name === IntegrationProviderEnum.Kastle
          ? cloneDeep(definition)
          : definition;

      if (
        workingDefinition.name ===
          IntegrationProviderEnum.BuildingEnginesPrism ||
        workingDefinition.name ===
          IntegrationProviderEnum.BuildingEnginesPrismVisitors
      ) {
        validateNestedBEConfig(workingDefinition);
      }

      if (
        workingDefinition.name === IntegrationProviderEnum.Vantify
      ) {
        validateVantifyConfig(workingDefinition);
      }

      if (workingDefinition.name === IntegrationProviderEnum.Kastle) {
        validateKastleConfig(workingDefinition);
      }

      const validation = yup
        .object()
        .shape(createShapeFromProperties(workingDefinition.properties));

      validation.validateSync(_channelIntegration.settings, {
        abortEarly: false,
      });

      if (workingDefinition.name === IntegrationProviderEnum.AccessManagement) {
        validateNestedConfig(_channelIntegration.settings);
      }

      setValidationError(null);

      return true;
    } catch (err) {
      console.error('error while validating config', err);
      setValidationError(err);

      return false;
    }
  }

  const removePrevIgsValueFromTenantMappings = (
    channelIntegrationSetting: any
  ) => {
    channelIntegrationSetting.accessControlSettings.tenantMappings =
      channelIntegrationSetting.accessControlSettings?.tenantMappings.map(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ prevIgsValue, ...item }: TenantMappings) => ({
          ...item,
        })
      );
  };

  const convertTenantMappingsToUUID = (channelIntegrationSetting: any): void => {
    const tenantMappings = channelIntegrationSetting?.accessControlSettings?.tenantMappings;

    if (tenantMappings && Array.isArray(tenantMappings)) {
        tenantMappings.forEach((mapping: any) => {
        if (mapping.vtsTenantId) {
          mapping.vtsTenantId = convertToUUID(mapping.vtsTenantId);
        }
      });
    }
  };

  async function doCreate() {
    if (!validateChannelIntegrationSettings()) {
      // we have errors, abort mutation!
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const acsProviderValue =
        _channelIntegration?.settings?.accessControlService?.value;
      const channelIntegrationSetting = _channelIntegration?.settings;

      if (
        geneaVisitorFeatureEnabled &&
        acsProviderValue === AccessControlServiceEntity.Genea &&
        channelIntegrationSetting?.accessControlSettings?.tenantMappings
          ?.length > 0
      ) {
        removePrevIgsValueFromTenantMappings(channelIntegrationSetting);
      }

      if (
        acsProviderValue === AccessControlServiceEntity.Kastle &&
        channelIntegrationSetting?.accessControlSettings?.tenantMappings
          ?.length > 0
      ) {
        removePrevIgsValueFromTenantMappings(channelIntegrationSetting);
      }

      if (tenantMappingConvertToUUIDEnabled) {
        convertTenantMappingsToUUID(channelIntegrationSetting);
      }

      const toCreate = {
        channel: _channelIntegration.channel,
        integration: {
          _id: _channelIntegration.integration._id,
        },
        settings: channelIntegrationSetting,
      };

      await getClient().mutate({
        mutation: createChannelIntegrationMutation,
        variables: {
          channelIntegration: toCreate,
        },
      });

      window.Toast.show(
        t('web.admin.channel.integrations.create.success', {
          integrationName: integration.name,
        })
      );
      updateChannelGroupRoles(integration.name);
      setIsConfigActive(true);
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      history.replace(`/l/channel/${channel.slug}/admin/integrations`);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  async function doSave() {
    if (!validateChannelIntegrationSettings()) {
      // we have errors, abort mutation!
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await pause(500);

      const acsProviderValue =
        _channelIntegration?.settings?.accessControlService?.value;
      let channelIntegrationSetting = { ..._channelIntegration?.settings };

      if (
        geneaVisitorFeatureEnabled &&
        acsProviderValue === AccessControlServiceEntity.Genea &&
        channelIntegrationSetting?.accessControlSettings?.tenantMappings
          ?.length > 0
      ) {
        removePrevIgsValueFromTenantMappings(channelIntegrationSetting);
      }

      if (
        acsProviderValue === AccessControlServiceEntity.Kastle &&
        channelIntegrationSetting?.accessControlSettings?.tenantMappings
          ?.length > 0
      ) {
        removePrevIgsValueFromTenantMappings(channelIntegrationSetting);
      }

      // If somebody disables the Apple Wallet feature flag later on, we need to disable it in the Kastle integration settings if it's enabled previously and user re-saves the integration
      if (
        !kastleAppleWalletFFEnabled &&
        definition?.name === IntegrationProviderEnum.Kastle &&
        channelIntegrationSetting?.isAppleWalletEnabled
      ) {
        channelIntegrationSetting.isAppleWalletEnabled = false;
      }

      // If somebody disables the Google Wallet feature flag later on, we need to disable it in the Kastle integration settings if it's enabled previously and user re-saves the integration
      if (
        !kastleGoogleWalletFFEnabled &&
        definition?.name === IntegrationProviderEnum.Kastle &&
        channelIntegrationSetting?.isGoogleWalletEnabled
      ) {
        channelIntegrationSetting.isGoogleWalletEnabled = false;
      }

      // If the Card Format feature is disabled later on, drop the stored
      // value so a stale card format isn't persisted on re-save.
      if (
        !cardFormatFFEnabled &&
        definition?.name === IntegrationProviderEnum.Kastle &&
        channelIntegrationSetting?.cardFormat
      ) {
        delete channelIntegrationSetting.cardFormat;
      }

      // Re-apply the Kastle wallet/card-format invariants on save so a loaded
      // (but un-edited) integration is persisted in a consistent state.
      if (
        cardFormatFFEnabled &&
        definition?.name === IntegrationProviderEnum.Kastle
      ) {
        channelIntegrationSetting = normalizeKastleWalletSettings(
          channelIntegrationSetting
        );
      }

      if (tenantMappingConvertToUUIDEnabled) {
        convertTenantMappingsToUUID(channelIntegrationSetting);
      }

      const toSave = {
        _id: _channelIntegration._id,
        channel: _channelIntegration.channel,
        integration: {
          _id: _channelIntegration.integration._id,
        },
        settings: channelIntegrationSetting,
      };

      setIsChannelIntegrationUpdated(false);
      const update = await getClient().mutate({
        mutation: updateChannelIntegrationMutation,
        variables: {
          channelIntegration: toSave,
        },
      });

      updateChannelIntegration(update.data.updateChannelIntegration);
      updateChannelGroupRoles(integration.name);
      setIsConfigActive(true);
      setIsChannelIntegrationUpdated(true);
      window.Toast.show(
        t('web.admin.channel.integrations.update.success', {
          integrationName: integration.name,
        })
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  async function onSave() {
    if (forCreate) {
      await doCreate();
    } else {
      await doSave();
    }

    refetchChannelFeatures();
  }

  const onTest = async () => {
    const config = _channelIntegration.settings;

    setError(null);
    setLoading(true);

    try {
      const integrationToVerify =
        TESTABLE_INTEGRATIONS_CONFIG[integration.name];

      if (!isEmpty(integrationToVerify.configFieldsToReplace)) {
        Object.entries(integrationToVerify.configFieldsToReplace).forEach(
          ([keyToReplace, originalKey]) => {
            config[keyToReplace] = config[originalKey];
          }
        );
      }

      await getClient().query({
        query: verifyConfigQuery,
        fetchPolicy: 'network-only',
        variables: {
          provider: integrationToVerify.provider,
          channelId: channel?._id,
          config,
        },
      });

      window.Toast.show(
        t('web.admin.channel.integrations.test.success', {
          integrationName: integration.name,
        })
      );
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  async function updateChannelGroupRoles(integrationName: string) {
    if (isEmpty(CHANNEL_INTEGRATION_PERMISSION_UPDATES[integrationName])) {
      return;
    }

    for (const intPermissions of CHANNEL_INTEGRATION_PERMISSION_UPDATES[
      integrationName
    ]) {
      const modulePermissions: string[] = intPermissions.permissions || [];

      try {
        const groupRole = {
          externalId: channel?._id,
          externalType: 'Channel',
          keys: ['externalId', 'externalType'],
          permissions: {
            [intPermissions.action]: modulePermissions,
          },
        };

        await getClient().mutate({
          mutation: updateChannelGroupRolesMutation,
          variables: {
            groupRole,
          },
        });
        window.Toast.show(t('web.admin.channel.update.group.role.success'));
      } catch (err) {
        setError(err);
      }
    }
  }

  async function deactivateIntegration() {
    const integrationName = definition?.friendlyName || 'integration';

    try {
      setIsDeleting(true);
      setIsActivating(true);

      await getClient().mutate({
        mutation: softDeleteChannelIntegration,
        variables: {
          id: channelIntegration._id,
        },
      });

      setIntegrationIsDeleted(true);

      if (
        integrationContent &&
        'contentsForIntegration' in integrationContent &&
        integrationContent.contentsForIntegration.length > 0
      ) {
        const { contentsForIntegration } = integrationContent;

        await Promise.all(
          contentsForIntegration.map(content => {
            return getClient().mutate({
              mutation: unPublishContent,
              variables: {
                id: safeConvertToUUID(content._id),
              },
            });
          })
        );
      }

      setChannelIntegration((prevChannelIntegration: any) => ({
        ...prevChannelIntegration,
        // perform an optimistic update by updating the integration
        // object in state and on page load the correct
        // integration object will replace the one in state
        deletedAt: new Date(),
      }));
      setIsActivating(false);
      setIsConfigActive(false);

      window.Toast.show(
        t('web.admin.channel.integrations.delete.success', { integrationName })
      );
    } catch (err) {
      const errorMessage =
        t('web.admin.channel.integrations.delete.failure', {
          integrationName,
        }) + err;

      window.Toast.show(errorMessage);
    } finally {
      setIsDeleting(false);
      setIsActivating(false);
    }
  }

  if (!_channelIntegration) {
    return <Loading />;
  }

  return (
    <AdminPage className={styles.ChannelIntegrationEdit}>
      <Flex mb={6}>
        {!forCreate &&
        _channelIntegration?.integration?.name === 'AccessManagement' ? (
          <Flex gap={2} align="end">
            <H3>{definition?.friendlyName || 'Integration'}</H3>
            <Chip
              size="xs"
              type={isConfigActive ? ChipStyle.Green : ChipStyle.Red}
              value={
                isConfigActive
                  ? 'web.admin.channel.integrations.status.active'
                  : 'web.admin.channel.integrations.status.inactive'
              }
            />
          </Flex>
        ) : null}
        <Flex gap={2} align="end" style={{ marginLeft: 'auto' }}>
          {deleteIntegrationFeatureEnabled &&
            !forCreate &&
            _channelIntegration?.integration?.name === 'AccessManagement' && (
              <Button
                loading={isActivating}
                testId="deleteIntegration"
                onClick={async () => {
                  if (integrationIsDeleted) {
                    setIntegrationIsDeleted(false);
                  } else {
                    setIsModalOpen(true);
                  }
                }}
                variant="secondary"
              >
                {integrationIsDeleted
                  ? t('web.admin.channel.integrations.button.activate')
                  : t('web.admin.channel.integrations.button.deactivate')}
              </Button>
            )}
          <Button
            testId="testIntegration"
            loading={loading}
            onClick={onTest}
            variant="secondary"
            style={{ display: isTestConfigButtonVisible ? 'block' : 'none' }}
          >
            {t`web.admin.channel.integrations.button.test`}
          </Button>
          <Button
            testId="createIntegration"
            loading={loading}
            onClick={() => onSave()}
            variant="primary"
            disabled={
              (forCreate && !_channelIntegration?.integration?._id) ||
              integrationIsDeleted
            }
          >
            {forCreate
              ? t`web.admin.channel.integrations.button.create`
              : t`web.admin.channel.integrations.button.save`}
          </Button>
        </Flex>
      </Flex>
      <ErrorMessage error={error} />
      {forCreate && (
        <section className={styles.edit}>
          <IntegrationDropdown
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'never'.
            testId="selectIntegration"
            onChange={changeIntegration}
            value={_channelIntegration?.integration?._id}
            channel={channel}
          />
        </section>
      )}
      {definition && (
        <section className={styles.edit}>
          <ValidationErrorContext.Provider value={validationError}>
            <ChannelIntegrationEditorRenderer
              forCreate={forCreate}
              integrationIsDeleted={integrationIsDeleted}
              definition={definition}
              channel={channel}
              isChannelIntegrationUpdated={isChannelIntegrationUpdated}
              channelIntegration={_channelIntegration}
              onUpdateChannelIntegration={updateChannelIntegration}
              integration={integration}
              {...(integration?.name === IntegrationProviderEnum.Vantify && {
                validationErrors: validationError,
                getAuthToken,
                webhookNotificationsEnabledFF: vantifyWebhookNotificationsEnabledFF,
              })}
            />
          </ValidationErrorContext.Provider>
        </section>
      )}
      <DeleteIntegrationModal
        isLoading={isDeleting}
        channelIntegration={_channelIntegration}
        channelIntegrationName={definition?.friendlyName || 'integration'}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onDeleteIntegration={() => deactivateIntegration()}
      />
    </AdminPage>
  );
}
